/**
 * /policies/travel/destinations — Session 3c PR 22.
 * Owning agent: Tashghil.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { DestinationItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function TravelDestinationsPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-travel-destinations", le],
    queryFn: () => session3cApi.listDestinations(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<DestinationItem>[] = [
    {
      headerKey: "policies.travel_destinations.col.name",
      label: "Destination",
      render: (r) => <span className="font-medium">{r.destination_name_en}</span>,
    },
    {
      headerKey: "policies.travel_destinations.col.code",
      label: "Code",
      render: (r) => <span className="font-mono text-xs">{r.destination_code}</span>,
    },
    {
      headerKey: "policies.travel_destinations.col.country",
      label: "Country",
      render: (r) => <span className="text-xs text-text-secondary">{r.country}</span>,
    },
    {
      headerKey: "policies.travel_destinations.col.tier",
      label: "Tier",
      render: (r) => <span className="text-xs uppercase">{r.category_tier}</span>,
    },
  ];

  return (
    <PolicyListPage<DestinationItem>
      title={t("policies.travel_destinations.title", "Travel Destinations")}
      subtitle={t("policies.travel_destinations.subtitle", "14 KSA cities + 13 international destinations + custom (per M3X.TRAVEL).")}
      agent="tashghil"
      domain="travel_destinations"
      newButtonLabel={t("policies.travel_destinations.cta.new", "New Destination")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.travel_destinations.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.travel_destinations.suggestion.clean_body", "Destinations feed the per-diem matrix and booking class rules. Per-diem amounts live on /policies/travel/per-diem."),
          primaryActionLabel: t("policies.travel_destinations.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "travel-destinations-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.travel_destinations.title", "Travel Destinations"),
        window_label: t("policies.travel_destinations.window", "active destinations"),
      }}
    />
  );
}
